import {
  CASE_CREATE_TEAM_BEGIN,
  CASE_CREATE_TEAM_SUCCESS,
  CASE_CREATE_TEAM_FAILURE,
  CASE_CREATE_TEAM_DISMISS_FEEDBACK,
} from './constants';
import { useCallback } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { call, put, takeLatest } from 'redux-saga/effects';
import {
  createPromiseAction,
  resolvePromiseAction,
  rejectPromiseAction,
} from '@adobe/redux-saga-promise';
import api from 'common/api';
import { withCurrentCaseId } from '../../../common/selectors';
import { addItemImmutable } from 'utils/arrays';

export const createTeam = createPromiseAction(CASE_CREATE_TEAM_BEGIN);

export function dismissCreateTeamFeedback() {
  return {
    type: CASE_CREATE_TEAM_DISMISS_FEEDBACK,
  };
}

export function* doCreateTeam(action) {
  const { caseId, team } = action.payload;
  let res = yield call(api.post, `/cases/${caseId}/teams`, team);

  if (res && res.error) {
    yield put({
      type: CASE_CREATE_TEAM_FAILURE,
      feedback: { message: 'feedback.createTeamFailure', error: res.error, retryAction: action },
    });
    return yield call(rejectPromiseAction, action, res.error);
  }

  yield put({
    type: CASE_CREATE_TEAM_SUCCESS,
    data: { team: res },
  });
  yield call(resolvePromiseAction, action, res);
}

export function* watchCreateTeam() {
  yield takeLatest(createTeam, withCurrentCaseId(doCreateTeam));
}

export function useCreateTeam() {
  const dispatch = useDispatch();

  const { createTeamPending, createTeamFeedback } = useSelector(
    (state) => ({
      createTeamPending: state.case.createTeamPending,
      createTeamFeedback: state.case.createTeamFeedback,
    }),
    shallowEqual,
  );

  const boundAction = useCallback(
    (...args) => {
      return dispatch(createTeam(...args));
    },
    [dispatch],
  );

  const boundDismissFeedback = useCallback(() => {
    return dispatch(dismissCreateTeamFeedback());
  }, [dispatch]);

  return {
    createTeam: boundAction,
    createTeamPending,
    createTeamFeedback,
    dismissCreateTeamFeedback: boundDismissFeedback,
  };
}

export function reducer(state, action) {
  switch (action.type) {
    case CASE_CREATE_TEAM_BEGIN + '.TRIGGER':
      // Just after a request is sent
      return {
        ...state,
        createTeamPending: true,
        createTeamFeedback: null,
      };

    case CASE_CREATE_TEAM_SUCCESS:
      // The request is success
      return {
        ...state,
        createTeamPending: false,
        createTeamFeedback: action.feedback,
        teams: addItemImmutable(state.teams, action.data.team),
      };

    case CASE_CREATE_TEAM_FAILURE:
      // The request is failed
      return {
        ...state,
        createTeamPending: false,
        createTeamFeedback: action.feedback,
      };

    case CASE_CREATE_TEAM_DISMISS_FEEDBACK:
      // Dismiss the request failure error
      return {
        ...state,
        createTeamFeedback: null,
      };

    default:
      return state;
  }
}
